import type {
  IAccount,
  IAddress,
  IProperty,
  IPropertySelection,
  IUserContext,
} from "@origin-digital/event-dispatcher";
import { BackendCrm, StorageKeys } from "@origin-digital/platform-enums";
import { isPresent } from "../utils/utils";
import {
  fetchAuthenticatedUser,
  fetchKrakenServiceStatuses,
} from "./fetchUserContext";

export { IAccount, IAddress, IProperty, IUserContext as IUser };

export type IServiceStatusResponse = Array<{
  backendServiceId: string;
  isActive: boolean;
  accountStatus?: string;
}>;

const KRAKEN_INVALID_ACCOUNT_STATUSES = ["INCOMPLETE", "WITHDRAWN"];

const storage = (): Storage => window.localStorage;

const storageKey = StorageKeys.selectedProperty;

export const readPropertyFromStorage = (): IPropertySelection | null => {
  const json = storage().getItem(storageKey);
  if (json) {
    return JSON.parse(json); // Note this is not validated as a correctly shaped type.
  }
  return null;
};

export const writePropertyToStorage = (property: IPropertySelection | null) => {
  try {
    storage().setItem(storageKey, JSON.stringify(property));
  } catch (error) {}
};

export const enrichKrakenServices = (
  user: IUserContext,
  serviceStatuses?: IServiceStatusResponse
): IUserContext => {
  const krakenServiceByBackendServiceId: Record<
    string,
    { accountStatus: string | undefined; isActive: boolean }
  > = (serviceStatuses ?? []).reduce(
    (result, { backendServiceId, accountStatus, isActive }) => ({
      ...result,
      [backendServiceId]: {
        accountStatus,
        isActive,
      },
    }),
    {}
  );

  user.accounts.forEach((account) => {
    account.services = account.services
      .map((service) => {
        const krakenService =
          krakenServiceByBackendServiceId[service.backendServiceId];

        // return existing data
        if (!krakenService) {
          return service;
        }

        const { accountStatus, isActive } = krakenService;

        // kraken service with invalid account status should not appear
        if (
          accountStatus &&
          KRAKEN_INVALID_ACCOUNT_STATUSES.includes(accountStatus)
        ) {
          return undefined;
        }

        // kraken service is closed
        if (!isActive) {
          service.serviceStatus = "CLOSED";
        }

        return service;
      })
      .filter(isPresent);
  });

  return user;
};

/**
 * Enrich user data with additional information
 *
 * Tactical fix for the account status: [RX-2311](https://origindd.atlassian.net/browse/RX-2311)
 */
export const enrichUserData = async (
  user: IUserContext
): Promise<IUserContext | undefined> => {
  try {
    // get backendServiceId of KRAKEN accounts only
    const krakenBackendServiceIds = user.accounts
      .filter((account) => account.backend === BackendCrm.KRAKEN)
      .flatMap((account) =>
        account.services.map((service) => service.backendServiceId)
      );

    if (krakenBackendServiceIds.length === 0) {
      return user;
    }

    const statusByBackendServiceId = await fetchKrakenServiceStatuses(
      krakenBackendServiceIds
    );

    return enrichKrakenServices(user, statusByBackendServiceId);
  } catch {
    // fallback to the existing user data if any errors
    return user;
  }
};

export const fetchUserContextV3 = async (): Promise<
  IUserContext | undefined
> => {
  return await fetchAuthenticatedUser();
};

export const getAccountNumberFromURI = (location: Location) => {
  const accountNumberPath = location.pathname
    ?.split("/")
    ?.find((p) => p.match(/A-\w{8*/) || p.match(/\d{6,10}/));
  const params = new URLSearchParams(location.search);
  const accountNumberQuery =
    params.get("accountNumber") || params.get("customerNumber"); // customer number used by LPG
  return accountNumberQuery || accountNumberPath;
};
